import { Component, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

import { CartService } from '@app/core/services/cart/cart.service';

import { BaseComponent } from '@app/core/components/base/base.component';
import { BottomStripComponent } from '../bottom-strip/bottom-strip.component';

import { cartStrip, BottomStripDetail } from '@app/shared/models/bottom-strip';
import { ActionType } from '@app/shared/enums/bottom-strip';

@Component({
    selector: 'app-bottom-strip-carousel',
    templateUrl: './bottom-strip-carousel.component.html',
    styleUrls: ['./bottom-strip-carousel.component.scss'],
    standalone: true,
    imports: [BottomStripComponent]
})
export class BottomStripCarouselComponent extends BaseComponent {
    activeCarouselId = 0;
    isAnimating = false;
    interval: any;
    indicators: number[] = [];
    carouselElement: HTMLElement | null = null;
    slideWidth: number;

    @Input() bottomStrips: BottomStripDetail[] = [];
    @Output() showFeedback = new EventEmitter<string>();

    destroySubscription$: Subject<void> = new Subject();
    hammer: HammerManager;

    constructor(private cartService: CartService) {
        super();
    }

    ngOnInitBrowser() {
        this.handleCartStrip();
        this.slideWidth = this.isMobile ? window.innerWidth : 400;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.bottomStrips) {
            this.reset();
        }
    }

    handleCartStrip() {
        if (this.isMobile) {
            this.cartService.state.pipe(takeUntil(this.destroySubscription$)).subscribe(() => {
                const cartItemPresent = Object.values(this.cartService.state.value.items).length;
                if (!cartItemPresent) {
                    this.stripRemoved(ActionType.Cart);
                } else {
                    this.addCartStrip();
                }
            });
        }
    }

    addCartStrip() {
        const cartStripPresent = this.bottomStrips.find((strip) => strip.button.action_type === ActionType.Cart);
        if (!cartStripPresent) {
            this.bottomStrips.unshift(cartStrip);
            this.reset();
        }
    }

    ngAfterViewInitBrowser() {
        this.initialiseHammer();
        this.updateIndicators();
        this.startAutoScroll();
        this.handleClickEvent();
    }

    initialiseHammer() {
        this.carouselElement = document.getElementById('bottom-carousel') as HTMLElement;
        this.hammer = new Hammer(this.carouselElement);
        this.hammer.on('swipeleft swiperight', (event) => {
            this.handleSwipe(event);
        });
    }

    // disable click when animation is in progress, this happens when user swipes in desktop, at the end click event get triggered
    handleClickEvent() {
        this.carouselElement?.addEventListener(
            "click",
            (event) => {
                if (this.isAnimating) {
                    event.stopPropagation();
                }
            },
            true
        );
    }

    stripRemoved(action_type: ActionType) {
        this.bottomStrips = this.bottomStrips.filter(strip => strip.button.action_type !== action_type);
        this.reset();

        // Stop auto-scroll if no items remain
        if (this.bottomStrips.length === 0) {
            this.clearAutoScroll();
        }
    }

    reset() {
        // Reset to first item
        this.activeCarouselId = 0;
        this.isAnimating = false;
        this.resetCarouselPosition();
        this.updateIndicators();
        this.restartAutoScroll();
    }

    showFeedbackModal(tracking_id: string) {
        this.showFeedback.emit(tracking_id);
    }

    handleSwipe(event: HammerInput) {
        if (this.isAnimating) return;
        this.restartAutoScroll();

        const count = this.bottomStrips.length;
        const translateX = this.getTranslateValue();

        if (event.type === 'swiperight' && this.activeCarouselId > 0) {
            this.animateCarousel(translateX + this.slideWidth, -1);
        } else if (event.type === 'swipeleft' && this.activeCarouselId < count - 1) {
            this.animateCarousel(translateX - this.slideWidth, 1);
        }
    }

    animateCarousel(newPosition: number, step: number) {
        this.isAnimating = true;
        this.activeCarouselId += step;
        this.carouselElement.style.transform = `translateX(${newPosition}px)`;
        this.carouselElement.addEventListener('transitionend', () => (this.isAnimating = false), { once: true });
        this.updateIndicators();
    }

    resetCarouselPosition() {
        if (!this.carouselElement) return;
        const newPosition = -this.activeCarouselId * this.slideWidth;
        this.carouselElement.style.transform = `translateX(${newPosition}px)`;
    }

    getTranslateValue() {
        if (!this.carouselElement) return 0;
        const matrix = new WebKitCSSMatrix(window.getComputedStyle(this.carouselElement).transform);
        return matrix.m41;
    }

    startAutoScroll() {
        if (this.bottomStrips.length > 1) {
            this.interval = setInterval(() => this.autoScroll(), 4500);
        }
    }

    restartAutoScroll() {
        this.clearAutoScroll();
        this.startAutoScroll();
    }

    autoScroll() {
        if (!this.carouselElement || this.isAnimating) return;
        const count = this.bottomStrips.length;
        const translateX = this.getTranslateValue();

        if (this.activeCarouselId < count - 1) {
            this.animateCarousel(translateX - this.slideWidth, 1);
        } else {
            this.animateCarousel(0, -this.activeCarouselId);
        }
    }

    updateIndicators() {
        this.indicators = Array(this.bottomStrips.length).fill(0);
    }

    clearAutoScroll() {
        if (this.interval) clearInterval(this.interval);
    }

    ngOnDestroyBrowser() {
        this.hammer?.destroy();
        this.destroySubscription$.next();
        this.destroySubscription$.complete();
        this.clearAutoScroll();
    }
}
